import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import ImageLightbox from './ImageLightbox';
import CarouselPauseButton from './CarouselPauseButton';

const carouselImages = [
  { src: '/images/flamme-olympique/carousel-1.webp', alt: 'Cyrille Lecourt avec les cheerleaders Cheers Up Dance devant le bus Paris 2024' },
  { src: '/images/flamme-olympique/carousel-2.webp', alt: 'Cyrille Lecourt entouré des cheerleaders Cheers Up Dance devant l\'entrée d\'un bâtiment en briques' },
  { src: '/images/flamme-olympique/carousel-3.webp', alt: 'Cheerleaders Cheers Up Dance devant le bus du Relais de la Flamme Olympique Paris 2024' },
  { src: '/images/flamme-olympique/carousel-4.webp', alt: 'Selfie des cheerleaders Cheers Up Dance avec la foule à Nogent-sur-Seine' },
  { src: '/images/flamme-olympique/carousel-5.webp', alt: 'Cheerleader Cheers Up Dance devant le bus officiel du Relais de la Flamme Olympique Paris 2024' },
];

interface Props {
  className?: string;
}

const FlammeOlympiqueCarousel = ({ className = '' }: Props) => {
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = carouselImages.length;

  const next = useCallback(() => setCurrent((c) => (c + 1) % total), [total]);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused || lightbox !== null) return;
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next, paused, lightbox]);

  const visibleIndices = [0, 1, 2].map((offset) => (current + offset) % total);

  return (
    <>
      <div className={`space-y-4 ${className}`}>
        <div className="max-w-2xl mx-auto relative">
          <button
            onClick={prev}
            className="absolute -left-10 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-background/90 shadow-md flex items-center justify-center text-foreground hover:bg-background transition-colors"
            aria-label="Photos précédentes"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="grid grid-cols-3 gap-2">
            {visibleIndices.map((idx, pos) => (
              <button
                type="button"
                key={pos}
                className="relative aspect-[3/4] rounded-xl overflow-hidden cursor-zoom-in hover:scale-[1.02] transition-transform group"
                onClick={() => setLightbox(idx)}
                aria-label="Agrandir la photo"
              >
                {carouselImages.map((img, imgIdx) => (
                  <img
                    key={imgIdx}
                    src={img.src}
                    alt={img.alt}
                    className={`w-full h-full object-cover ${imgIdx === idx ? 'block' : 'hidden'}`}
                  />
                ))}
                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <Maximize2 size={12} strokeWidth={1.5} />
                </span>
              </button>
            ))}
          </div>
          <button
            onClick={next}
            className="absolute -right-10 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-background/90 shadow-md flex items-center justify-center text-foreground hover:bg-background transition-colors"
            aria-label="Photos suivantes"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="flex justify-center">
          <CarouselPauseButton paused={paused} onToggle={() => setPaused((p) => !p)} />
        </div>

        {/* Vidéo — taille réduite */}
        <div className="max-w-2xl mx-auto rounded-xl overflow-hidden">
          <video
            src="/videos/flamme-olympique.mp4"
            controls
            preload="metadata"
            poster="/images/flamme-olympique/video-poster.webp"
            className="w-full rounded-xl aspect-video object-cover"
          >
            Votre navigateur ne supporte pas la lecture vidéo.
          </video>
        </div>
      </div>

      {lightbox !== null && (
        <ImageLightbox
          images={carouselImages}
          currentIndex={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((i) => ((i ?? 0) - 1 + total) % total)}
          onNext={() => setLightbox((i) => ((i ?? 0) + 1) % total)}
        />
      )}
    </>
  );
};

export default FlammeOlympiqueCarousel;
